import { IMPORT_ERROR, MAX_ROWS } from './importLimits.js';

/** A file that cannot be imported; `code` is one of `IMPORT_ERROR`. */
export class ImportParseError extends Error {
  constructor(code) {
    super(code);
    this.name = 'ImportParseError';
    this.code = code;
  }
}

/** Rows inspected to find the header; titles above it are usually a few lines. */
const HEADER_SCAN_ROWS = 10;
const HEADER_FILL_RATIO = 0.5;

const clean = (cell) => String(cell ?? '').trim();
const filledCount = (row) => row.filter((cell) => clean(cell) !== '').length;

/**
 * The header is the first row that is about as full as the fullest of the first rows, so
 * title or note lines above it (a single cell) are skipped: "column names on row 3".
 */
function findHeaderIndex(matrix) {
  const counts = matrix.slice(0, HEADER_SCAN_ROWS).map(filledCount);
  const max = Math.max(0, ...counts);
  if (max === 0) return -1;
  const threshold = max >= 2 ? Math.max(2, Math.ceil(max * HEADER_FILL_RATIO)) : 1;
  return counts.findIndex((count) => count >= threshold);
}

const defaultColumnLabel = (position) => `Column ${position}`;

/** Blank names get `columnLabel(position)` (translated by the caller); repeated names get " (2)", " (3)" … */
function nameHeaders(cells, columnLabel) {
  const seen = new Map();
  return cells.map((raw, index) => {
    const base = clean(raw) || columnLabel(index + 1);
    const times = (seen.get(base) ?? 0) + 1;
    seen.set(base, times);
    return times === 1 ? base : `${base} (${times})`;
  });
}

/**
 * Turns the cell matrix of a sheet (any format) into the import payload. Pure; throws
 * `ImportParseError` (`empty`, `too_many_rows`).
 *
 * @param {Array<Array<string|number|null>>} matrix Rows as read from the file, blank lines included.
 * @param {{ columnLabel?: (position: number) => string }} [options] Name of a column whose header is blank.
 * @returns {{ headerRow: number, headers: string[], rows: Array<{ rowNumber: number, cells: string[] }>, rowCount: number }}
 */
export function buildSheet(matrix, { columnLabel = defaultColumnLabel } = {}) {
  const headerIndex = findHeaderIndex(matrix);
  if (headerIndex < 0) throw new ImportParseError(IMPORT_ERROR.EMPTY);

  const headers = nameHeaders(matrix[headerIndex], columnLabel);
  const rows = matrix
    .slice(headerIndex + 1)
    .map((cells, offset) => ({ cells, rowNumber: headerIndex + offset + 2 }))
    .filter(({ cells }) => filledCount(cells) > 0)
    .map(({ cells, rowNumber }) => ({
      rowNumber,
      cells: headers.map((_, column) => clean(cells[column])),
    }));

  if (rows.length === 0) throw new ImportParseError(IMPORT_ERROR.EMPTY);
  if (rows.length > MAX_ROWS) throw new ImportParseError(IMPORT_ERROR.TOO_MANY_ROWS);

  return { headerRow: headerIndex + 1, headers, rows, rowCount: rows.length };
}
